import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchGeolocation, submitLead, prepareLeadPayload, handleApiResponse } from '../utils/api';

/**
 * Custom hook for handling lead form submission with geolocation
 * @returns {Object} Form utilities and state
 */
export const useLeadForm = () => {
  const [geolocationData, setGeolocationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch geolocation on mount
  useEffect(() => {
    const getGeolocation = async () => {
      const data = await fetchGeolocation();
      if (data && data.isSuccess) {
        setGeolocationData(data);
      }
    };
    getGeolocation();
  }, []);

  /**
   * Submit form data
   * @param {Object} formData - Form data to submit
   * @param {Function} onSuccess - Optional success callback
   * @param {Function} onError - Optional error callback
   */
  const submitForm = async (formData, onSuccess, onError) => {
    setIsLoading(true);
    setError(null);

    try {
      // Prepare payload
      const payload = prepareLeadPayload(formData, geolocationData);

      // Submit to API
      const response = await submitLead(payload);

      // Handle response
      const result = handleApiResponse(response);

      if (result.success) {
        // Redirect to application page with customerId
        if (result.customerId) {
          navigate(`/application/main?customerid=${result.customerId}`);
        }
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        setError(result.message);
        if (onError) {
          onError(result);
        }
      }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      if (onError) {
        onError({ success: false, message: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    geolocationData,
    isLoading,
    error,
    submitForm,
    clearError: () => setError(null)
  };
};
